import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { activitiesAPI } from '../api/client';
import { Footprints, Coffee, Mountain, Bike, UtensilsCrossed, Landmark, MapPin } from 'lucide-react';
import Layout from '../components/Layout';
import '../styles/discover.css';

const ACTIVITY_ICONS = {
  run: Footprints,
  coffee: Coffee,
  hike: Mountain,
  cycling: Bike,
  dinner: UtensilsCrossed,
  museum: Landmark,
  other: MapPin,
};

function ActivityIcon({ type, size = 20, color = '#52AAD8' }) {
  const Icon = ACTIVITY_ICONS[type] || MapPin;
  return <Icon size={size} color={color} strokeWidth={2} />;
}

const TYPES = [
  { value: '', label: 'All' },
  { value: 'run', label: 'Run' },
  { value: 'coffee', label: 'Coffee' },
  { value: 'hike', label: 'Hike' },
  { value: 'cycling', label: 'Cycling' },
  { value: 'dinner', label: 'Dinner' },
  { value: 'museum', label: 'Museum' },
  { value: 'other', label: 'Other' },
];

function formatDate(str) {
  if (!str) return 'Flexible';
  return new Date(str).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function Discover() {
  const navigate = useNavigate();
  const isLoggedIn = !!localStorage.getItem('token');
  const [activities, setActivities] = useState([]);
  const [typeFilter, setTypeFilter] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    activitiesAPI.discover(typeFilter || undefined)
      .then((res) => setActivities(res.data || []))
      .catch(() => setActivities([]))
      .finally(() => setLoading(false));
  }, [typeFilter]);

  const handleCardClick = (a) => {
    if (isLoggedIn) {
      navigate(`/activity/${a.id}`);
    } else {
      navigate(`/register?redirect=/activity/${a.id}&intent=rsvp`);
    }
  };

  return (
    <Layout title="Discover">
    <div className="discover-container">
      <div className="discover-body">
      <div className="discover-body-inner">
        <div className="discover-page-header">
          <h1 className="discover-page-title">Discover</h1>
          <p className="discover-page-sub">Public plans happening around your network.</p>
        </div>

        <div className="filter-chips">
          {TYPES.map((t) => (
            <button
              key={t.value}
              className={`chip${typeFilter === t.value ? ' active' : ''}`}
              onClick={() => setTypeFilter(t.value)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="discover-empty">Loading…</p>
        ) : activities.length === 0 ? (
          <p className="discover-empty">No public activities right now — check back soon!</p>
        ) : (
          <div className="discover-list">
            {activities.map((a) => (
              <div key={a.id} className="discover-card" onClick={() => handleCardClick(a)}>
                <div className="discover-card-header">
                  <span className="discover-card-icon">
                    <ActivityIcon type={a.activity_type} size={20} />
                  </span>
                  <div>
                    <p className="discover-title">{a.title || a.activity_type}</p>
                    <p className="discover-host">by {a.creator_name}</p>
                  </div>
                </div>
                <div className="discover-meta">
                  <span className="discover-meta-item">{formatDate(a.start_time)}</span>
                  {a.location_name && <span className="discover-meta-item">{a.location_name}</span>}
                  {a.duration_minutes && <span className="discover-meta-item">{a.duration_minutes} min</span>}
                </div>
                {a.rsvp_count > 0 && (
                  <p className="discover-rsvp">{a.rsvp_count} {a.rsvp_count === 1 ? 'person' : 'people'} going</p>
                )}
              </div>
            ))}
          </div>
        )}

        {!isLoggedIn && (
          <div className="discover-join-cta">
            <p>Join Covo to RSVP, create your own plans, and coordinate with friends.</p>
            <button className="btn-primary" style={{ borderRadius: 12 }} onClick={() => navigate('/register')}>
              Get started — it's free
            </button>
          </div>
        )}
      </div>
    </div>
    </div>
    </Layout>
  );
}
